import React, { useState, useEffect } from 'react';
import StackItem from './StackItem';
import { useAuth, useQwanyx, dupQwanta } from 'qwanyx';
import { Capture } from './Capture'; // Import the updated Capture component
import { getChildrenItems } from './getChildrenItems'; // Import the data fetching function

export const StackContainer = ({
    rootId,
    debug = false
}) => {
    // Get the current user
    const { currentUser } = useAuth();

    // Use currentUser.id as rootId if rootId is null or empty
    const effectiveRootId = !rootId ? currentUser.id : rootId;

    // Get Qwanyx context for card visibility and update functionality
    const {
        cardRegistry,
        toggleCardVisibility
    } = useQwanyx();

    // Check if the "currentCard" exists and get its visibility state
    const isCurrentCardVisible = cardRegistry &&
        cardRegistry["currentCard"] &&
        cardRegistry["currentCard"].isVisible !== false;

    // Array to track all selected item IDs
    const [selectedItems, setSelectedItems] = useState([]);
    // Track the last selected item for shift-selection
    const [lastSelectedItem, setLastSelectedItem] = useState(null);
    // Store all visible items in a flat array to help with range selection
    const [flattenedItems, setFlattenedItems] = useState([]);

    // Store card data to use when updating the currentCard
    const [cardDataMap, setCardDataMap] = useState({});

    const [dragCompletedSignal, setDragCompletedSignal] = useState(0);

    // Updated refreshAfterDrop function to handle source folder refresh
    const refreshAfterDrop = (draggedItemId, targetId, sourceParentId) => {
        if (debug) {
            console.log(`Refreshing tree after drop: ${draggedItemId} moved from ${sourceParentId} to ${targetId}`);
        }

        // Clear the flattened items to force re-registration
        setFlattenedItems([]);

        // Store which folders need to be refreshed
        const foldersToRefresh = new Set();

        // Add source and target folders to refresh set
        if (sourceParentId) foldersToRefresh.add(sourceParentId);
        if (targetId) foldersToRefresh.add(targetId);

        // If we have the parent folders in our cardDataMap, mark them for refresh
        const updatedCardDataMap = { ...cardDataMap };

        // Signal to all items that a drag operation completed
        // This will be used by StackItem to know when to refresh
        setDragCompletedSignal(Date.now());

        if (debug) {
            console.log("Folders to refresh:", Array.from(foldersToRefresh));
        }
    };

    // Function to duplicate the selected item
    const handleDuplicateItem = async () => {
        // Check if we have any selected items
        if (selectedItems.length === 0) {
            return;
        }

        // We'll duplicate the first selected item (or you could loop through all selected items)
        const itemToDuplicate = selectedItems[0];

        // Get the parent ID from the cardDataMap
        const itemData = cardDataMap[itemToDuplicate];
        if (!itemData) {
            console.error("Item data not found for:", itemToDuplicate);
            return;
        }

        try {
            // Use dupQwanta to create a duplicate
            const result = await dupQwanta(itemToDuplicate);

            if (result.success && result.newId) {
                // Signal to refresh the parent folder to show the new item
                const parentId = itemData.ed; // 'ed' seems to be the parent ID in your data structure
                refreshAfterDrop(result.newId, parentId, null);

                // Optionally, select the newly created item
                setSelectedItems([result.newId]);

                if (debug) {
                    console.log(`Successfully duplicated item: ${itemToDuplicate} -> ${result.newId}`);
                }
            } else {
                console.error("Failed to duplicate item:", result.error);
            }
        } catch (error) {
            console.error("Error during duplication:", error);
        }
    };

    // Modified function to handle creating new items, now supports title parameter
    const handleNewItem = (sourceId, title) => () => {
        // Check if we have any selected items
        if (selectedItems.length === 0) {
            console.error("No item selected to get parent from");
            return;
        }

        // Get the first selected item
        const selectedItem = selectedItems[0];

        // Get the parent ID (ed) from the cardDataMap
        const itemData = cardDataMap[selectedItem];
        if (!itemData) {
            console.error("Item data not found for:", selectedItem);
            return;
        }

        // Get the ed (parent) from the selected item
        const parentId = itemData.ed;
        if (!parentId) {
            console.error("No parent ID (ed) found for selected item:", selectedItem);
            return;
        }

        try {
            // Create override properties including title if provided
            const overrideProps = { 
                ed: parentId
            };
            
            // Add title if provided
            if (title && title.trim()) {
                overrideProps.title = title.trim();
            }

            if (debug) {
                console.log("Creating new item with properties:", {
                    sourceId,
                    parentId,
                    overrideProps
                });
            }

            // Use dupQwanta to create a duplicate with the new parent and title
            dupQwanta(sourceId, null, overrideProps, false, title)
                .then(result => {
                    if (result.success && result.newId) {
                        // Signal to refresh the parent folder to show the new item
                        refreshAfterDrop(result.newId, parentId, null);

                        // Optionally, select the newly created item
                        setSelectedItems([result.newId]);

                        if (debug) {
                            console.log(`Successfully created new item from template: ${sourceId} -> ${result.newId} with parent: ${parentId}`);
                            if (title) {
                                console.log(`Assigned title: "${title}"`);
                            }
                        }
                    } else {
                        console.error("Failed to create new item:", result.error);
                    }
                })
                .catch(error => {
                    console.error("Error during new item creation:", error);
                });
        } catch (error) {
            console.error("Error initiating new item creation:", error);
        }
    };

    // Debug function to show alerts or console logs when debug mode is on
    const debugLog = (message, data) => {
        if (debug) {
            console.log(message, data);
            if (data) {
                alert(`DEBUG - ${message}:\n${typeof data === 'object' ?
                    Object.entries(data)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join('\n')
                    : data}`);
            } else {
                alert(`DEBUG - ${message}`);
            }
        }
    };

    // Fetch items data for the root item
    useEffect(() => {
        const fetchRootItemData = async () => {
            try {
                // Fetch children of the root item
                const children = await getChildrenItems(effectiveRootId);

                // Store each item's data in the cardDataMap
                const newDataMap = {};
                children.forEach(item => {
                    newDataMap[item._id] = item;
                });

                // Update the state with the new data map
                setCardDataMap(prev => ({
                    ...prev,
                    ...newDataMap
                }));

                if (debug) {
                    console.log("Fetched root item children:", children);
                }
            } catch (error) {
                console.error("Error fetching root item data:", error);
                if (debug) {
                    alert(`DEBUG - Error fetching root item data: ${error.message}`);
                }
            }
        };

        fetchRootItemData();
    }, [effectiveRootId, debug]);

    // Function to update flattened items list (called by child components when they render)
    const registerItem = (itemId, level, itemData) => {
        // This maintains a flat representation of the visible hierarchy for shift selection
        setFlattenedItems(prev => {
            const exists = prev.some(item => item.id === itemId);
            if (!exists) {
                return [...prev, { id: itemId, level }];
            }
            return prev;
        });

        // Store the item data if provided
        if (itemId && itemData) {
            setCardDataMap(prev => ({
                ...prev,
                [itemId]: itemData
            }));

            if (debug) {
                console.log(`Registered item ${itemId} with data:`, itemData);
            }
        }
    };

    // Function to toggle the visibility of the "currentCard"
    const handleToggleCurrentCard = () => {
        toggleCardVisibility("currentCard");
        if (debug) {
            debugLog("Toggled visibility of currentCard", {
                visible: !isCurrentCardVisible
            });
        }
    };

    const handleSelect = (itemId, isShiftKey, isCtrlKey) => {
        if (debug) {
            console.log("Selected item:", itemId);
        }

        if (isShiftKey && lastSelectedItem) {
            // Handle shift selection - select a range
            const startIdx = flattenedItems.findIndex(item => item.id === lastSelectedItem);
            const endIdx = flattenedItems.findIndex(item => item.id === itemId);

            if (startIdx >= 0 && endIdx >= 0) {
                const start = Math.min(startIdx, endIdx);
                const end = Math.max(startIdx, endIdx);

                const itemsInRange = flattenedItems.slice(start, end + 1).map(item => item.id);

                if (isCtrlKey) {
                    // Shift+Ctrl: Add range to existing selection
                    setSelectedItems(prev => {
                        const newSelection = [...prev];
                        itemsInRange.forEach(id => {
                            if (!newSelection.includes(id)) {
                                newSelection.push(id);
                            }
                        });
                        return newSelection;
                    });
                } else {
                    // Shift only: Replace selection with range
                    setSelectedItems(itemsInRange);
                }
            }
        } else if (isCtrlKey) {
            // Handle ctrl selection - toggle single item
            setSelectedItems(prev => {
                if (prev.includes(itemId)) {
                    return prev.filter(id => id !== itemId);
                } else {
                    return [...prev, itemId];
                }
            });
        } else {
            // Normal selection - select only this item
            setSelectedItems([itemId]);

            // Note: currentCard updating is now handled in StackItem component
        }

        // Update last selected item
        setLastSelectedItem(itemId);
    };

    return (
        <>
            <div className="p-1"
            > {/* Title */}
                <div className="has-text-centered" style={{ fontSize: '12px', fontWeight: 'light' }}>
                    Root Item
                    {debug && <span className="tag is-warning is-light ml-2">Debug Mode</span>}
                </div>
                
                {/* Multifunction Capture with new props */}
                <Capture 
                    onDuplicate={handleDuplicateItem}
                    onNewItem={handleNewItem}
                    selectedItems={selectedItems}
                />
            </div>
            <div style={{
                height: 'calc(100% - 85px)',
                overflow: 'auto',
                width: '350px',
                borderRight: '1px solid #eee'
            }}>
                <StackItem
                    itemId={effectiveRootId}
                    isRoot={true}
                    onSelect={handleSelect}
                    selectedItems={selectedItems}
                    registerItem={registerItem}
                    onDrop={refreshAfterDrop}
                    dragCompletedSignal={dragCompletedSignal}
                />

                {/* Optionally, you can show selection info */}
                {selectedItems.length > 0 && (
                    <div className="selection-info" style={{ marginTop: '10px', padding: '10px', background: '#f5f5f5' }}>
                        <div>Selected: {selectedItems.length} item(s)</div>
                        <small>IDs: {selectedItems.join(', ')}</small>
                    </div>
                )}
            </div>
        </>
    );
};

export default StackContainer;